/**
 * Context-aware presentation for Coverage Matrix report fields —
 * prioritises which empty fields to surface and what guidance to show
 * based on report type(s) and project intent.
 */
import type { ChecklistItem } from '../../config/documentChecklists';
import type { ReportTypeId } from '../../config/reportTypes';
import { REPORT_TYPE_REGISTRY } from '../../config/reportTypes';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { ProjectIntent } from '../../types/siteRag';
import { effectivePower, bucketMidpointMw } from '../projectIntent';
import { normaliseCooling } from '../../types/cooling';
import {
  extractFieldValue,
  formatFieldValue,
  resolveRequirementFields,
} from '../evidenceLineage/requirementToFields';

export interface FieldPresentationHint {
  key: string;
  /** Lower = show first. */
  priority: number;
  /** Replaces generic "Requires uploaded document" copy. */
  guidance: string;
  /** Regional / SED proxy — informational only, not a substitute for doc attestation. */
  proxyHint?: { label: string; displayValue: string; sourceLabel: string };
  /** Highlight as especially relevant to current report + intent. */
  recommended: boolean;
}

export interface RequirementFieldPresentation {
  /** Section intro above inline editors / empty field list. */
  intro: string;
  /** Empty field keys sorted by priority (most relevant first). */
  orderedFieldKeys: readonly string[];
  hintsByKey: Readonly<Record<string, FieldPresentationHint>>;
}

function primaryReportType(reportTypes: readonly ReportTypeId[]): ReportTypeId {
  return reportTypes[0] ?? 'feasibility';
}

function reportTypeLabel(id: ReportTypeId): string {
  return REPORT_TYPE_REGISTRY[id]?.shortLabel ?? id;
}

function reportTypePhrase(reportTypes: readonly ReportTypeId[]): string {
  if (reportTypes.length === 0) return 'this site';
  if (reportTypes.length === 1) return reportTypeLabel(reportTypes[0]!);
  const labels = reportTypes.slice(0, 2).map(reportTypeLabel);
  return reportTypes.length > 2
    ? `${labels.join(', ')}, +${reportTypes.length - 2} more`
    : labels.join(' + ');
}

function usesLiquidCooling(intent: ProjectIntent | undefined): boolean {
  const c = normaliseCooling(intent?.coolingStrategy);
  return c === 'closed-loop-liquid' || c === 'evaporative';
}

function isHighLoad(intent: ProjectIntent | undefined): boolean {
  const p = effectivePower(intent);
  // ≥50 MW. Prefer the exact MW; else the bucket's MW basis. Captures the new
  // ladder's high buckets and the legacy 'over-200mw' alike.
  const mw = p.isExact && p.mw != null ? p.mw : (bucketMidpointMw(p.bucket) ?? 0);
  return mw >= 50;
}

function isCampusScale(intent: ProjectIntent | undefined): boolean {
  const p = effectivePower(intent);
  // ≥200 MW campus.
  const mw = p.isExact && p.mw != null ? p.mw : (bucketMidpointMw(p.bucket) ?? 0);
  return mw >= 200;
}

/** Base priority (lower = more important). Unlisted keys sort last. */
const GEOTECH_BASE: Record<string, number> = {
  bearingCapacity_kPa: 20,
  soilClassification: 30,
  boreholeDepth_m: 40,
  groundwaterDepth_m: 50,
};

const UTILITY_BASE: Record<string, number> = {
  utilityName: 10,
  capacityOffered_MW: 15,
  interconnectionClass: 25,
  serviceDate: 35,
  costEstimate_USD: 45,
};

const FLOOD_BASE: Record<string, number> = {
  floodZone: 10,
  bfe_ft: 20,
  datum: 30,
  topOfLowestFloor_ft: 40,
};

function scoreGeotechField(
  fieldKey: string,
  reportTypes: readonly ReportTypeId[],
  intent: ProjectIntent | undefined,
): { priority: number; recommended: boolean; guidance: string } {
  let priority = GEOTECH_BASE[fieldKey] ?? 100;
  let recommended = false;
  const primary = primaryReportType(reportTypes);
  const power = effectivePower(intent);
  const powerLabel = power.displayLabel;

  if (fieldKey === 'bearingCapacity_kPa') {
    if ((primary === 'test-fit' || primary === 'feasibility') && isHighLoad(intent)) {
      priority -= 15;
      recommended = true;
    }
    if (isCampusScale(intent)) {
      priority -= 10;
      recommended = true;
    }
  }

  if (fieldKey === 'soilClassification') {
    if (primary === 'test-fit' || primary === 'feasibility' || primary === 'interconnection') {
      priority -= 10;
      recommended = true;
    }
  }

  if (fieldKey === 'groundwaterDepth_m') {
    if (primary === 'water-resource' || reportTypes.includes('water-resource')) {
      priority -= 35;
      recommended = true;
    }
    if (usesLiquidCooling(intent)) {
      priority -= 40;
      recommended = true;
    }
    if (primary === 'environmental') {
      priority -= 15;
    }
  }

  if (fieldKey === 'boreholeDepth_m') {
    if (primary === 'environmental') {
      priority -= 15;
      recommended = true;
    }
    if (primary === 'water-resource') {
      priority -= 10;
      recommended = true;
    }
  }

  const guidanceByKey: Record<string, string> = {
    bearingCapacity_kPa: isCampusScale(intent)
      ? `Foundation loads for ${powerLabel} need verified allowable bearing — enter lab value or upload geotech report.`
      : 'Allowable bearing from site-specific testing — upload geotech report or enter verified kPa.',
    soilClassification: 'USCS class from borehole logs drives foundation and seismic site-class assumptions.',
    boreholeDepth_m: primary === 'environmental'
      ? 'Borehole depth confirms subsurface conditions for environmental and foundation review.'
      : 'Minimum borehole depth from the geotechnical investigation.',
    groundwaterDepth_m: usesLiquidCooling(intent)
      ? `Liquid cooling (${reportTypePhrase(reportTypes)}) needs verified groundwater depth for dewatering and intake design.`
      : primary === 'water-resource'
        ? 'Primary input for water-resource assessment — upload aquifer/borehole study or enter measured depth.'
        : 'Measured groundwater depth from borehole logs — not inferred from regional maps.',
  };

  return {
    priority,
    recommended,
    guidance: guidanceByKey[fieldKey] ?? 'Upload supporting document or enter a verified value.',
  };
}

function scoreUtilityField(
  fieldKey: string,
  reportTypes: readonly ReportTypeId[],
  intent: ProjectIntent | undefined,
): { priority: number; recommended: boolean; guidance: string } {
  let priority = UTILITY_BASE[fieldKey] ?? 100;
  let recommended = false;
  const primary = primaryReportType(reportTypes);
  const power = effectivePower(intent);

  if (fieldKey === 'capacityOffered_MW') {
    if (primary === 'power-availability' || primary === 'interconnection') {
      priority -= 25;
      recommended = true;
    }
    if (power.isExact || power.bucket !== 'undecided') {
      priority -= 10;
      recommended = true;
    }
  }

  if (fieldKey === 'utilityName' && (primary === 'power-availability' || primary === 'interconnection')) {
    priority -= 15;
    recommended = true;
  }

  const guidanceByKey: Record<string, string> = {
    utilityName: 'Serving utility from the interconnection or service letter.',
    capacityOffered_MW: power.bucket !== 'undecided'
      ? `Utility-offered MW for your ${power.displayLabel} load — upload service letter or enter confirmed capacity.`
      : 'Confirmed MW from utility service letter or interconnection study.',
    interconnectionClass: 'Queue / cluster study class from the utility or ISO.',
    serviceDate: 'Date on the utility service letter or study.',
    costEstimate_USD: 'Interconnection cost estimate from utility letter or study.',
  };

  return {
    priority,
    recommended,
    guidance: guidanceByKey[fieldKey] ?? 'Upload utility letter or enter verified value.',
  };
}

function scoreFloodField(
  fieldKey: string,
  reportTypes: readonly ReportTypeId[],
): { priority: number; recommended: boolean; guidance: string } {
  let priority = FLOOD_BASE[fieldKey] ?? 100;
  let recommended = false;
  const primary = primaryReportType(reportTypes);

  if (fieldKey === 'floodZone' && (primary === 'feasibility' || primary === 'test-fit')) {
    priority -= 15;
    recommended = true;
  }
  if (fieldKey === 'bfe_ft' && primary === 'environmental') {
    priority -= 10;
    recommended = true;
  }

  const guidanceByKey: Record<string, string> = {
    floodZone: 'FEMA flood zone from LOMA/LOMR or elevation certificate — regional FIRM is context only.',
    bfe_ft: 'Base flood elevation from certified elevation certificate.',
    datum: 'Vertical datum used on the elevation certificate.',
    topOfLowestFloor_ft: 'Lowest finished floor elevation relative to BFE.',
  };

  return {
    priority,
    recommended,
    guidance: guidanceByKey[fieldKey] ?? 'Upload flood certificate or enter verified value.',
  };
}

function scoreGenericField(
  fieldKey: string,
  requirementId: string,
): { priority: number; recommended: boolean; guidance: string } {
  return {
    priority: 50,
    recommended: false,
    guidance: requirementId.includes('doc')
      ? 'Upload the supporting document or enter verified values below.'
      : 'Enter verified value or upload supporting document.',
  };
}

function scoreField(
  requirementId: string,
  fieldKey: string,
  reportTypes: readonly ReportTypeId[],
  intent: ProjectIntent | undefined,
): { priority: number; recommended: boolean; guidance: string } {
  switch (requirementId) {
    case 'geotech':
      return scoreGeotechField(fieldKey, reportTypes, intent);
    case 'utility-letter':
      return scoreUtilityField(fieldKey, reportTypes, intent);
    case 'flood-cert':
      return scoreFloodField(fieldKey, reportTypes);
    default:
      return scoreGenericField(fieldKey, requirementId);
  }
}

function buildProxyHint(
  requirementId: string,
  fieldKey: string,
  sed: SiteEvidenceDocument | undefined,
): FieldPresentationHint['proxyHint'] | undefined {
  if (!sed) return undefined;
  const related = resolveRequirementFields(sed, requirementId, true);

  if (requirementId === 'geotech') {
    if (fieldKey === 'bearingCapacity_kPa' || fieldKey === 'soilClassification') {
      const sdc = related.find(f => f.path === 'seismic.designCategory' && !f.isEmpty)
        ?? related.find(f => f.path === 'seismic.pga' && !f.isEmpty);
      if (sdc) {
        return {
          label: 'Regional seismic (proxy)',
          displayValue: formatFieldValue(sdc.value),
          sourceLabel: sdc.sourceLabel ?? 'USGS / provider',
        };
      }
    }
    if (fieldKey === 'groundwaterDepth_m') {
      const ws = extractFieldValue(sed, 'waterStress.stressLevel');
      const wsFallback = ws.isEmpty && sed?.waterStress?.data?.sourceEdition !== '4.0'
        ? extractFieldValue(sed, 'waterStress.overallWaterRisk')
        : ws;
      if (!wsFallback.isEmpty) {
        return {
          label: 'Regional water stress (proxy)',
          displayValue: formatFieldValue(wsFallback.value),
          sourceLabel: 'Provider estimate — not site-specific depth',
        };
      }
    }
  }

  if (requirementId === 'utility-letter') {
    if (fieldKey === 'capacityOffered_MW') {
      const cap = related.find(f => f.path === 'grid.capacity' && !f.isEmpty);
      if (cap) {
        return {
          label: 'Grid proxy capacity',
          displayValue: formatFieldValue(cap.value),
          sourceLabel: cap.sourceLabel ?? 'HIFLD / provider',
        };
      }
    }
    if (fieldKey === 'utilityName') {
      const util = related.find(f => f.path === 'grid.utilityName' && !f.isEmpty);
      if (util) {
        return {
          label: 'Nearest utility (proxy)',
          displayValue: formatFieldValue(util.value),
          sourceLabel: util.sourceLabel ?? 'Provider',
        };
      }
    }
  }

  if (requirementId === 'flood-cert' && fieldKey === 'floodZone') {
    const zone = related.find(f => f.path === 'flood.floodZone' && !f.isEmpty);
    if (zone) {
      return {
        label: 'Regional FIRM zone (proxy)',
        displayValue: formatFieldValue(zone.value),
        sourceLabel: zone.sourceLabel ?? 'FEMA provider',
      };
    }
  }

  return undefined;
}

function buildSectionIntro(
  requirement: ChecklistItem,
  reportTypes: readonly ReportTypeId[],
  intent: ProjectIntent | undefined,
  emptyCount: number,
): string {
  const rtPhrase = reportTypePhrase(reportTypes);
  const power = effectivePower(intent);
  const powerClause = power.bucket !== 'undecided'
    ? ` for ${power.displayLabel}`
    : '';

  if (requirement.id === 'geotech') {
    const primary = primaryReportType(reportTypes);
    if (primary === 'water-resource' || usesLiquidCooling(intent)) {
      return `For ${rtPhrase}${powerClause}, groundwater depth is the highest-priority geotech field. ${emptyCount} field${emptyCount === 1 ? '' : 's'} can be entered manually or sourced from an uploaded report.`;
    }
    if (isHighLoad(intent)) {
      return `For ${rtPhrase}${powerClause}, bearing capacity and soil class anchor foundation design. Regional seismic data may appear as context — site-specific geotech still required.`;
    }
    return `Fields below are ranked for ${rtPhrase}${powerClause}. Enter verified values or upload a geotechnical report.`;
  }

  if (requirement.id === 'utility-letter') {
    return `Ranked for ${rtPhrase}${powerClause}. Provider grid data may appear as proxy context — utility letter values override estimates.`;
  }

  if (requirement.id === 'flood-cert') {
    return `For ${rtPhrase}, certified flood elevations take precedence over regional FIRM zones shown as proxy context.`;
  }

  return `${emptyCount} field${emptyCount === 1 ? '' : 's'} for ${rtPhrase}${powerClause} — enter verified values or upload supporting documents.`;
}

export function resolveRequirementFieldPresentation(args: {
  requirement: ChecklistItem;
  reportTypes: readonly ReportTypeId[];
  projectIntent?: ProjectIntent;
  sed?: SiteEvidenceDocument;
  emptyFieldKeys: readonly string[];
}): RequirementFieldPresentation {
  const { requirement, reportTypes, projectIntent, sed, emptyFieldKeys } = args;

  const scored = emptyFieldKeys.map(key => {
    const { priority, recommended, guidance } = scoreField(
      requirement.id,
      key,
      reportTypes,
      projectIntent,
    );
    const hint: FieldPresentationHint = {
      key,
      priority,
      recommended,
      guidance,
      proxyHint: buildProxyHint(requirement.id, key, sed),
    };
    return hint;
  });

  scored.sort((a, b) => a.priority - b.priority || a.key.localeCompare(b.key));

  const hintsByKey: Record<string, FieldPresentationHint> = {};
  for (const h of scored) hintsByKey[h.key] = h;

  return {
    intro: buildSectionIntro(requirement, reportTypes, projectIntent, emptyFieldKeys.length),
    orderedFieldKeys: scored.map(h => h.key),
    hintsByKey,
  };
}
